import React, { useEffect, useMemo, useState } from "react";
import { executeMutation, executeQuery } from "../../../graphql/client";

// #144 Leads tab: everyone who STARTED signup but never made an
// account — the follow-up queue the weekly funnel digest points at.
// Mark-contacted is a toggle so the list works like an inbox.

const chip = (bg, color, border) => ({
  display: "inline-block",
  background: bg,
  color,
  border: `1px solid ${border}`,
  borderRadius: 999,
  padding: "1px 8px",
  fontSize: 11,
  fontWeight: 700,
  marginLeft: 6,
});

const FILTERS = [
  { id: "todo", label: "Needs follow-up" },
  { id: "contacted", label: "Contacted" },
  { id: "converted", label: "Converted" },
];

const LeadsTab = () => {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("todo");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const out = await executeQuery(`query SignupLeads { signupLeads }`);
      setData(out?.signupLeads ? JSON.parse(out.signupLeads) : null);
    } catch (err) {
      setError(err.message || "Couldn't load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleContacted = async (lead) => {
    setBusyId(lead.userId);
    try {
      await executeMutation(
        `mutation MarkLeadContacted($userId: ID!, $contacted: Boolean!) {
          markLeadContacted(userId: $userId, contacted: $contacted)
        }`,
        { userId: lead.userId, contacted: !lead.contactedAt }
      );
      await load();
    } catch (err) {
      setError(err.message || "Couldn't update lead.");
    } finally {
      setBusyId("");
    }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    if (filter === "converted") return data.converted || [];
    const stalled = data.stalled || [];
    return filter === "contacted"
      ? stalled.filter((l) => l.contactedAt)
      : stalled.filter((l) => !l.contactedAt);
  }, [data, filter]);

  const counts = useMemo(() => {
    const stalled = data?.stalled || [];
    return {
      todo: stalled.filter((l) => !l.contactedAt).length,
      contacted: stalled.filter((l) => l.contactedAt).length,
      converted: (data?.converted || []).length,
    };
  }, [data]);

  return (
    <div style={{ maxWidth: 760 }}>
      <h2 style={{ marginTop: 0 }}>🔁 Signup Leads</h2>
      <p style={{ color: "#6a7385", fontSize: 14 }}>
        People who started signup (name/email/phone captured) but never
        finished creating an account. A quick text or email here is the
        cheapest signup you'll ever get — the weekly funnel digest
        counts this same list.
      </p>

      {error && (
        <div
          style={{
            background: "#fdecea",
            border: "1px solid #e7b3ba",
            color: "#b00020",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            style={{
              border:
                filter === f.id
                  ? "2px solid var(--ab-ocean, #0e7c9b)"
                  : "1px solid #d7dbe4",
              background: filter === f.id ? "var(--ab-seafoam, #e7f4f3)" : "#fff",
              color: "#333",
              borderRadius: 999,
              padding: "6px 14px",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {f.label} ({counts[f.id]})
          </button>
        ))}
        <button
          type="button"
          onClick={load}
          disabled={loading}
          style={{
            border: "1px solid #d7dbe4",
            background: "#f6f7fa",
            color: "#333",
            borderRadius: 999,
            padding: "6px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {!loading && rows.length === 0 && (
        <p style={{ color: "#6a7385" }}>
          {filter === "todo"
            ? "🎉 No stalled leads — everyone who started signup finished."
            : "Nothing here yet."}
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((lead) => (
          <div
            key={lead.userId}
            style={{
              border: "1px solid #e6e9f0",
              borderRadius: 10,
              background: "#fff",
              padding: "10px 14px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div style={{ minWidth: 0, flex: "1 1 220px" }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {lead.name || "(no name)"}
                {lead.consented && (
                  <span style={chip("#eef7ee", "#1f4f1d", "#b9dcb5")}>
                    ✓ consented
                  </span>
                )}
                {lead.paid && (
                  <span style={chip("#fdf2e3", "#7a4b00", "#f0d47a")}>
                    💳 paid
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13, color: "#5b6b7c", wordBreak: "break-all" }}>
                {lead.email && (
                  <a href={`mailto:${lead.email}`} style={{ marginRight: 10 }}>
                    {lead.email}
                  </a>
                )}
                {lead.phone && <a href={`tel:${lead.phone}`}>{lead.phone}</a>}
              </div>
              <div style={{ fontSize: 12, color: "#8a93a3" }}>
                Started{" "}
                {lead.daysAgo === 0
                  ? "today"
                  : lead.daysAgo === 1
                  ? "yesterday"
                  : `${lead.daysAgo} days ago`}
                {lead.contactedAt &&
                  ` · contacted ${new Date(lead.contactedAt).toLocaleDateString()}`}
              </div>
            </div>
            {filter !== "converted" && (
              <button
                type="button"
                onClick={() => toggleContacted(lead)}
                disabled={busyId === lead.userId}
                style={{
                  border: "none",
                  background: lead.contactedAt
                    ? "#f6f7fa"
                    : "var(--ab-ocean, #0e7c9b)",
                  color: lead.contactedAt ? "#333" : "#fff",
                  borderRadius: 8,
                  padding: "8px 14px",
                  fontWeight: 700,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                {busyId === lead.userId
                  ? "Saving…"
                  : lead.contactedAt
                  ? "Undo contacted"
                  : "✓ Mark contacted"}
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LeadsTab;
